/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  margin: 50px;
  padding: 2rem;
  background: #fff;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  border-radius: 8px;
  display:flex;
  flex-direction: column;
  align-items:center;
  text-align: center;
  gap:20px;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const FieldGroup = styled.div`
  margin-bottom: 1.5rem;
  width: 100%;
  color: #385963;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  input,
  textarea {
    width: 100%;
    padding: 0.6rem;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 1rem;
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }

  p {
    margin: 0.3rem 0 0;
    padding: 10px;
    background: #f3f4f6;
    border-radius: 6px;
    word-wrap: break-word;
    white-space: pre-wrap;
    width: 100%;
    max-width: 100%;
    text-align: left;
    max-height: 500px;
    overflow-y: auto;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 5rem;

  @media (max-width: 768px) {
     flex-direction: column;
     gap: 0.5rem;
  }
`;


const Button = styled.button`
  background-color: #385963;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 2rem;
  place-self: center;

  &:hover {
    background-color: #2c4a52;
  }
`;

const AboutPieriaInfo = () => {
  const { t } = useTranslation();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const token = sessionStorage.getItem("token");

  const emptyField = { en: '', gr: '' };
  const emptyStat = { label: { ...emptyField }, number: '' };

  const [form, setForm] = useState({
    description1: { ...emptyField },
    description2: { ...emptyField },
    link: '',
    stat1: { ...emptyStat },
    stat2: { ...emptyStat },
    stat3: { ...emptyStat },
    stat4: { ...emptyStat },
  });
  const [originalForm, setOriginalForm] = useState(form);
  const [isEditing, setIsEditing] = useState(false);
  const [pieriaId, setPieriaId] = useState(null);

  // Fetch Pieria info
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${BASE_URL}PieriaInfo`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          console.warn('Failed to fetch Pieria info:', res.status);
          return;
        }

        let data;
        try { data = await res.json(); } catch { data = null; }

        if (data && data.length > 0) {
          const info = data[0];
          setPieriaId(info.id);
          setForm({
            description1: info.description1 || { ...emptyField },
            description2: info.description2 || { ...emptyField },
            link: info.link || '',
            stat1: info.stat1 || { ...emptyStat },
            stat2: info.stat2 || { ...emptyStat },
            stat3: info.stat3 || { ...emptyStat },
            stat4: info.stat4 || { ...emptyStat },
          });
          setOriginalForm({
            description1: info.description1 || { ...emptyField },
            description2: info.description2 || { ...emptyField },
            link: info.link || '',
            stat1: info.stat1 || { ...emptyStat },
            stat2: info.stat2 || { ...emptyStat },
            stat3: info.stat3 || { ...emptyStat },
            stat4: info.stat4 || { ...emptyStat },
          });
        }
      } catch (err) {
        console.error('Fetch error:', err);
      }
    };
    fetchData();
  }, [BASE_URL, token]);

  const handleChange = (field, value, lang = null) => {
    if (lang) {
      setForm((prev) => ({
        ...prev,
        [field]: { ...prev[field], [lang]: value },
      }));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleEditToggle = () => setIsEditing(true);
  const handleCancel = () => {
    setForm(originalForm);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!pieriaId) return;

    try {
      const res = await fetch(`${BASE_URL}PieriaInfo/${pieriaId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setOriginalForm(form);
        setIsEditing(false);
        alert('Saved successfully!');
      } else {
        alert('Failed to save.');
      }
    } catch (err) {
      console.error(err);
      alert('Error saving data.');
    }
  };

  // Helper to safely render data or fallback
  const renderField = (value, type = 'text') => {
    if (!value) {
      if (type === 'text') return t('noDataAvailable');
      if (type === 'multi') return { en: t('noDataAvailable'), gr: t('noDataAvailable') };
      if (type === 'stat') return { label: { en: t('noDataAvailable'), gr: t('noDataAvailable') }, number: t('noDataAvailable') };
    }

    if (type === 'text') return value || t('noDataAvailable');
    if (type === 'multi') return { en: value.en || t('noDataAvailable'), gr: value.gr || t('noDataAvailable') };
    if (type === 'stat') return {
      label: { en: value.label?.en || t('noDataAvailable'), gr: value.label?.gr || t('noDataAvailable') },
      number: value.number || t('noDataAvailable')
    };

    return value;
  };

  return (
    <Container>
      <FieldGroup>
        <label>Google Drive Link</label>
        {isEditing ? (
          <input
            type="url"
            value={form.link}
            onChange={(e) => handleChange('link', e.target.value)}
          />
        ) : (
          <p>{renderField(form.link, 'text')}</p>
        )}
      </FieldGroup>

      {/* Description 1 */}
      <FieldGroup>
        <label>{t('description')} 1 (Ελληνικά)</label>
        {isEditing ? (
          <textarea
            value={form.description1.gr}
            onChange={(e) => handleChange('description1', e.target.value, 'gr')}
          />
        ) : (
          <p>{renderField(form.description1, 'multi').gr}</p>
        )}
      </FieldGroup>
      <FieldGroup>
        <label>{t('description')} 1 (English)</label>
        {isEditing ? (
          <textarea
            value={form.description1.en}
            onChange={(e) => handleChange('description1', e.target.value, 'en')}
          />
        ) : (
          <p>{renderField(form.description1, 'multi').en}</p>
        )}
      </FieldGroup>
    

      {/* Description 2 */}
       <FieldGroup>
        <label>{t('description')} 2 (Ελληνικά)</label>
        {isEditing ? (
          <textarea
            value={form.description2.gr}
            onChange={(e) => handleChange('description2', e.target.value, 'gr')}
          />
        ) : (
          <p>{renderField(form.description2, 'multi').gr}</p>
        )}
      </FieldGroup>
      <FieldGroup>
        <label>{t('description')} 2 (English)</label>
        {isEditing ? (
          <textarea
            value={form.description2.en}
            onChange={(e) => handleChange('description2', e.target.value, 'en')}
          />
        ) : (
          <p>{renderField(form.description2, 'multi').en}</p>
        )}
      </FieldGroup>
     

      {/* Stats */}
      {['stat1','stat2','stat3','stat4'].map((statKey, idx) => {
        const stat = renderField(form[statKey], 'stat');
        return (
          <FieldGroup key={statKey}>
           <label>{t('statistic')} {idx + 1} {t('label')} (Ελληνικά)</label>
            {isEditing ? (
              <input
                value={form[statKey].label.gr}
                onChange={(e) => handleChange(statKey, e.target.value, 'gr')}
              />
            ) : (
              <p>{stat.label.gr}</p>
            )}

            <label>{t('statistic')} {idx + 1} {t('label')} (English)</label>
            {isEditing ? (
              <input
                value={form[statKey].label.en}
                onChange={(e) => handleChange(statKey, e.target.value, 'en')}
              />
            ) : (
              <p>{stat.label.en}</p>
            )}

            <label>{t('statistic')} {idx + 1} Number</label>
            {isEditing ? (
              <input
                value={form[statKey].number}
                onChange={(e) => handleChange(statKey, e.target.value)}
              />
            ) : (
              <p>{stat.number}</p>
            )}
          </FieldGroup>
        );
      })}

      <ButtonRow>
        {!isEditing ? (
          <Button onClick={handleEditToggle}>{t('edit')}</Button>
        ) : (
          <>
            <Button onClick={handleSave}>{t('save')}</Button>
            <Button onClick={handleCancel}>{t('cancel')}</Button>
          </>
        )}
      </ButtonRow>
    </Container>
  );
};

export default AboutPieriaInfo;
